import { useContext, useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { FirebaseError } from '@firebase/util';
import { db } from '../api/firebase/config';
import { AuthContext } from '../contexts/auth';
import { useCompanyId } from './useCompanyId';
import { isCompanyAdmin, isOperatorStaff } from '../utils/permission';

/**
 * メール取り込み機能へのアクセス可否を判定する。
 * 運営スタッフが企業単位でトグル（`email_import_enabled`）を ON にした企業のみ利用できる。
 * プラン種別には依存しない（フリープランでもトグル ON なら利用可。有料プラン検討中の試用を想定）。
 *
 * - companyAdmin / 代理モード / 運営スタッフ: `email_import_enabled = true` なら true
 * - その他のロールでは常に false
 *
 * `loading` は企業ドキュメントの初回取得が完了するまで true（ルートガードのちらつき防止に使う）。
 */
export const useEmailImportAccess = (): { loading: boolean; allowed: boolean } => {
  const { user } = useContext(AuthContext);
  const { companyId: effectiveCompanyId, isProxyMode } = useCompanyId();
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const companyIdToUse = effectiveCompanyId ?? user?.company_id;
    if (!companyIdToUse) {
      setEnabled(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    const ref = doc(db, 'companies', companyIdToUse);
    const unsubscribe = onSnapshot(
      ref,
      snapshot => {
        if (!snapshot.exists()) {
          setEnabled(false);
          setLoading(false);
          return;
        }
        const data = snapshot.data();
        setEnabled(data.email_import_enabled === true);
        setLoading(false);
      },
      error => {
        // ログアウト直後の permission-denied は正常動作
        if (error instanceof FirebaseError && error.code === 'permission-denied') {
          setLoading(false);
          return;
        }
        setLoading(false);
      },
    );
    return (): void => unsubscribe();
  }, [effectiveCompanyId, user?.company_id]);

  if (!user || !enabled) return { loading, allowed: false };
  // 企業管理者・代理モード・運営スタッフのみメニュー/ページを利用できる（ロール制御は維持）
  const allowed = isCompanyAdmin(user.role) || isProxyMode || isOperatorStaff(user.role);
  return { loading, allowed };
};

/**
 * 右上アカウントメニューに「メール取り込み」導線を出すかどうか。
 * アクセス可否と同一ルール。メニューは読み込み中は非表示でよいので loading は無視する。
 */
export const useEmailMenuVisibility = (): boolean => useEmailImportAccess().allowed;
